import type { PlainOpeningHoursDto, PlainSectionDto } from '@ordr-as/menu-builder-types/epos';

const DAY_NAMES = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'] as const;

/**
 * Check if a section is currently open based on its opening hours.
 * Returns true if:
 * - The section has no opening hours restrictions (hasOpeningHours === false)
 * - The current time (in the location's timezone) falls within the section's opening hours
 *
 * @param section - The section to check
 * @param zoneId - The location's timezone (e.g., "Europe/Oslo")
 */
export function isSectionOpen(section: PlainSectionDto, zoneId: string): boolean {
  if (!section.hasOpeningHours) {
    return true;
  }
  return isWithinOpeningHours(section.openingHours, new Date(), zoneId);
}

/**
 * Check if the given time falls within any of the opening hours.
 * Handles day rollover (e.g., restaurant open until 2 AM).
 *
 * @param hours - Opening hours configuration
 * @param currentTime - Current time (Date object)
 * @param zoneId - The location's timezone (e.g., "Europe/Oslo")
 */
export function isWithinOpeningHours(hours: PlainOpeningHoursDto[], currentTime: Date, zoneId: string): boolean {
  if (hours.length === 0) {
    return true;
  }

  const { dayName: currentDayName, timeMinutes: currentTimeMinutes } = getLocalTimeInfo(currentTime, zoneId);
  const previousDayName = getPreviousDayName(currentDayName);

  // Check today's hours
  const todayHours = hours.find((h) => h.day === currentDayName);
  if (todayHours && !todayHours.closed) {
    if (isTimeWithinRange(currentTimeMinutes, todayHours.timeRange.startTime, todayHours.timeRange.endTime, false)) {
      return true;
    }
  }

  // Check if we're in the rollover period from previous day's hours
  const previousDayHours = hours.find((h) => h.day === previousDayName);
  if (previousDayHours && !previousDayHours.closed && previousDayHours.timeRange.endsOnNextDay) {
    // For rollover, we need to check if current time is before the end time
    if (isTimeWithinRange(currentTimeMinutes, '00:00', previousDayHours.timeRange.endTime, true)) {
      return true;
    }
  }

  return false;
}

/**
 * Get local time information for a given timezone.
 * Uses Intl.DateTimeFormat to convert the time to the specified timezone.
 */
function getLocalTimeInfo(date: Date, zoneId: string): { dayName: string; timeMinutes: number } {
  // Get the day of week in the specified timezone
  const dayFormatter = new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: zoneId });
  const dayName = dayFormatter.format(date).toUpperCase();

  // Get hours and minutes in the specified timezone
  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
    timeZone: zoneId,
  });
  const timeParts = timeFormatter.formatToParts(date);
  const hour = parseInt(timeParts.find((p) => p.type === 'hour')?.value ?? '0', 10);
  const minute = parseInt(timeParts.find((p) => p.type === 'minute')?.value ?? '0', 10);

  return {
    dayName,
    timeMinutes: hour * 60 + minute,
  };
}

/**
 * Get the previous day name.
 */
function getPreviousDayName(dayName: string): string {
  const index = DAY_NAMES.indexOf(dayName as typeof DAY_NAMES[number]);
  if (index === -1) return 'SATURDAY'; // fallback
  const previousIndex = (index + 6) % 7;
  return DAY_NAMES[previousIndex]!;
}

/**
 * Parse time string (HH:MM) to minutes since midnight.
 */
function parseTimeToMinutes(time: string): number {
  const parts = time.split(':').map(Number);
  const hours = parts[0] ?? 0;
  const minutes = parts[1] ?? 0;
  return hours * 60 + minutes;
}

/**
 * Check if the current time in minutes falls within a time range.
 * @param currentMinutes - Current time in minutes since midnight
 * @param startTime - Start time as HH:MM string
 * @param endTime - End time as HH:MM string
 * @param isRolloverCheck - If true, only checks from midnight to end time
 */
function isTimeWithinRange(
  currentMinutes: number,
  startTime: string,
  endTime: string,
  isRolloverCheck: boolean
): boolean {
  const startMinutes = parseTimeToMinutes(startTime);
  const endMinutes = parseTimeToMinutes(endTime);

  if (isRolloverCheck) {
    // For rollover, we're checking the next day portion (midnight to end time)
    return currentMinutes < endMinutes;
  }

  // Special case: 00:00 to 00:00 means "open all day"
  // Backend treats this as 00:00 to 23:59:59.999 due to minusNanos(1) wrapping
  if (startMinutes === 0 && endMinutes === 0) {
    return true;
  }

  // Normal range check (start <= current < end)
  // If end is less than start, it means it rolls over to next day
  if (endMinutes < startMinutes) {
    // Hours like 18:00 - 02:00: we're open if current >= start (on same day portion)
    return currentMinutes >= startMinutes;
  }

  return currentMinutes >= startMinutes && currentMinutes < endMinutes;
}
